import React, { useState } from 'react';
import { AlertTriangle, ArrowRight, CalendarCheck, CalendarClock, Check, ChevronDown, FileSearch, HelpCircle, Lightbulb, Megaphone, PartyPopper, TrendingUp, Unlock, UserCheck, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { AttentionTarget } from '../../utils/requestDashboard';
import type { BriefingBadge, BriefingDraft, BriefingItem } from '../../utils/territorialBriefing';
interface BriefingCardProps {
  item: BriefingItem;
  sentKinds: string[];
  onPrepare: (draft: BriefingDraft) => void;
  onOpen: (target: AttentionTarget) => void;
  wide?: boolean;
}
const BADGES: Record<BriefingBadge, {
  label: Localized;
  className: string;
  icon: typeof TrendingUp;
}> = {
  important: {
    label: {
      fr: 'Important',
      en: 'Important'
    },
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: AlertTriangle
  },
  rising: {
    label: {
      fr: 'En hausse',
      en: 'Rising'
    },
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: TrendingUp
  },
  unblock: {
    label: {
      fr: 'À débloquer',
      en: 'To unblock'
    },
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: Unlock
  },
  followup: {
    label: {
      fr: 'Suivi attendu',
      en: 'Follow-up expected'
    },
    className: 'bg-indigo-50 text-[#4F46E5] border-indigo-200',
    icon: Clock
  },
  anticipate: {
    label: {
      fr: 'À anticiper',
      en: 'To anticipate'
    },
    className: 'bg-sky-50 text-sky-700 border-sky-200',
    icon: CalendarClock
  },
  share: {
    label: {
      fr: 'À partager',
      en: 'To share'
    },
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: PartyPopper
  }
};

/**
 * One quick action per brief, picked from its type: results to share and
 * things to anticipate lead to citizen information; issues lead to a
 * follow-up request.
 */
function quickActions(item: BriefingItem): BriefingDraft[] {
  const preferred = item.badge === 'share' || item.badge === 'anticipate' ? 'citizen' : 'followup';
  const draft = item.drafts.find(d => d.kind === preferred) ?? item.drafts[0];
  return draft ? [draft] : [];
}
function Label({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1" data-id="element-5177" data-mp="2g58szpjvsrir">
      {children}
    </div>;
}

/**
 * One situation. At rest: title, short facts, commitment, next step and
 * actions. "Voir le détail" reveals why it matters and the explanation.
 */
export function BriefingCard({
  item,
  sentKinds,
  onPrepare,
  onOpen,
  wide
}: BriefingCardProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const badge = BADGES[item.badge];
  const BadgeIcon = badge.icon;
  const isShare = item.badge === 'share';
  const exp = item.explanation;
  const detailsId = `briefing-${item.id}-details`;
  return <article className={`border rounded-xl bg-white flex flex-col ${isShare ? 'border-emerald-200' : 'border-slate-200'} ${wide ? 'lg:flex-row' : ''}`} data-id="element-5178" data-mp="1sd24tdob7quf">
      <div className={`p-4 flex flex-col gap-3 flex-1 min-w-0 ${wide ? 'lg:border-r lg:border-slate-100' : ''}`} data-id="element-5179" data-mp="14r8766mermce">
        <div data-id="element-5180" data-mp="1irj0rhf3rf19">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badge.className}`} data-id="element-5181" data-mp="1ir95bebagfo6">
            <BadgeIcon className="w-3 h-3" data-id="element-5182" data-mp="1rpkwbhg2hfh5" />
            {badge.label[lang]}
          </span>
          <h3 className="mt-2 text-[15px] font-bold text-[#0F172A] font-heading leading-snug" data-id="element-5183" data-mp="1npu91npr1hhd">
            {item.title[lang]}
          </h3>
          <p className={`mt-1 text-sm text-slate-600 leading-relaxed ${open ? '' : 'line-clamp-2'}`} data-id="element-5184" data-mp="21fo1eg0gqjvu">
            {item.summary[lang]}
          </p>
        </div>

        {item.engagement && <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2" data-id="element-5185" data-mp="2bev9anofhmbk">
            <div className="flex items-center justify-between gap-2 text-xs" data-id="element-5186" data-mp="26h3kw3mzsic4">
              <span className="flex items-center gap-1.5 font-semibold text-[#4F46E5] min-w-0" data-id="element-5187" data-mp="3rty61oduf6y">
                <CalendarCheck className="w-3.5 h-3.5 shrink-0" data-id="element-5188" data-mp="8qwriov02wz3" />
                <span className="truncate" data-id="element-5189" data-mp="diya0sirhjxk">{item.engagement.label[lang]}</span>
              </span>
              <span className="font-semibold text-[#0F172A] shrink-0" data-id="element-5190" data-mp="ihhyazktv7t3">
                {item.engagement.dueDate[lang]} · {item.engagement.progress} %
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-white overflow-hidden" role="progressbar" aria-valuenow={item.engagement.progress} aria-valuemin={0} aria-valuemax={100} aria-label={t('Avancement de l’engagement', 'Commitment progress')} data-id="element-5191" data-mp="3lyhzrhk84k7v">
              <div className="h-full bg-[#4F46E5] rounded-full" style={{
            width: `${item.engagement.progress}%`
          }} data-id="element-5192" data-mp="166anmd6y2heq" />
            </div>
          </div>}

        {open && <div id={detailsId} className="flex flex-col gap-3" data-id="element-5193" data-mp="2vozcviahd7bt">
            <div data-id="element-5194" data-mp="2vqahfkbr2j3z">
              <Label data-id="element-5195" data-mp="2qpmrl1z9c8bc">{t('Pourquoi cela mérite votre attention', 'Why it deserves your attention')}</Label>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-5196" data-mp="2lyef1x2ms6ty">{item.why[lang]}</p>
            </div>
            {exp && <div className={`rounded-lg px-3 py-2 text-sm border ${exp.status === 'unknown' ? 'bg-slate-50 border-dashed border-slate-300' : 'bg-slate-50 border-slate-200'}`} data-id="element-5197" data-mp="2gyjo5tiuvh8w">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-0.5" data-id="element-5198" data-mp="22z0lhg8tcy3z">
                  {exp.status === 'unknown' ? <HelpCircle className="w-3.5 h-3.5 text-slate-400" data-id="element-5199" data-mp="1y32103qlw08u" /> : <FileSearch className="w-3.5 h-3.5 text-slate-500" data-id="element-5200" data-mp="16clrgvdossy" />}
                  {exp.label ? exp.label[lang] : exp.status === 'documented' ? t('Explication documentée', 'Documented explanation') : exp.status === 'partial' ? t('Explication partielle', 'Partial explanation') : t('Cause inconnue', 'Unknown cause')}
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-5201" data-mp="1lqrcq2hw8207">{exp.text[lang]}</p>
                {exp.source && <p className="text-xs text-slate-400 mt-1" data-id="element-5202" data-mp="29gtvtt3txxmo">{exp.source[lang]}</p>}
              </div>}
            {item.engagement && <p className="text-xs text-slate-500" data-id="element-5203" data-mp="1zie9e3fqzcji">
                {item.engagement.owner[lang]}, {item.engagement.confirmedOn[lang]} ·{' '}
                {item.engagement.progressLabel[lang]}
              </p>}
          </div>}

        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls={detailsId} className="self-start inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#0F172A]" data-id="element-5204" data-mp="o4tw6an2q6qw">
          {open ? t('Masquer le détail', 'Hide details') : t('Voir le détail', 'Show details')}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-5205" data-mp="3g9q4crfpu47t" />
        </button>
      </div>

      <div className={`p-4 flex flex-col gap-2.5 border-t ${isShare ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-100 bg-slate-50/60'} ${wide ? 'lg:border-t-0 lg:w-72 shrink-0' : 'mt-auto'} rounded-b-xl ${wide ? 'lg:rounded-bl-none lg:rounded-r-xl' : ''}`} data-id="element-5206" data-mp="3c6trlhcux2bs">
        <div data-id="element-5207" data-mp="2xd8i2pjmywiy">
          <Label data-id="element-5208" data-mp="2skxgzjd5t4ot">
            <Lightbulb className="w-3 h-3" data-id="element-5209" data-mp="2skfa7ssvf82n" />
            {t('Prochaine étape suggérée', 'Suggested next step')}
          </Label>
          <p className="text-sm text-[#0F172A] leading-snug" data-id="element-5210" data-mp="24tjmp7vg6z17">{item.nextStep[lang]}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-5211" data-mp="20szqc6ip7zg4">
          {quickActions(item).map(d => {
          const sent = sentKinds.includes(`${item.id}:${d.kind}`);
          const Icon = sent ? Check : d.kind === 'followup' ? UserCheck : Megaphone;
          const primary = true;
          return <button key={d.kind} onClick={() => !sent && onPrepare(d)} disabled={sent} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${sent ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : primary ? 'bg-[#4F46E5] hover:bg-[#4338CA] text-white' : 'text-slate-700 border border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-5212" data-mp="2ynf61lpkf9oi">
                <Icon className="w-3.5 h-3.5" data-id="element-5213" data-mp="3rf7618leod6z" />
                {sent ? d.kind === 'followup' ? t('Demande envoyée', 'Request sent') : t('Transmise aux Communications', 'Sent to Communications') : d.kind === 'followup' ? t('Demande de suivi', 'Follow-up request') : t('Information citoyenne', 'Citizen information')}
              </button>;
        })}
        </div>
        <button onClick={() => onOpen(item.link.target)} className="self-start inline-flex items-center gap-1 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5214" data-mp="3si7johi84yhn">
          {item.link.label[lang]}
          <ArrowRight className="w-3.5 h-3.5" data-id="element-5215" data-mp="o4r7cmmzrcv9" />
        </button>
      </div>
    </article>;
}